import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
import { Video as VideoIcon } from "lucide-react";

/**
 * Popup that resolves a demo video for an exercise by name.
 * Uses GET /api/exercise-video?name=... (cached exercise or live YouTube search).
 */
export const ExerciseVideoDialog = ({ name, open, onOpenChange }) => {
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !name) return;
    setLoading(true);
    setVideo(null);
    api.get("/exercise-video", { params: { name } })
      .then(({ data }) => setVideo(data))
      .catch(() => setVideo({ placeholder: true }))
      .finally(() => setLoading(false));
  }, [open, name]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover border-border max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <VideoIcon className="h-5 w-5 text-[#ccff0c]" /> {name}
          </DialogTitle>
        </DialogHeader>
        <div data-testid="exercise-video-dialog">
          {loading ? (
            <Skeleton className="w-full rounded-xl bg-card" style={{ aspectRatio: "16/9" }} />
          ) : video && video.embed_url ? (
            <div>
              <AspectRatio ratio={16 / 9}>
                <iframe
                  title={name}
                  src={video.embed_url}
                  className="w-full h-full rounded-xl"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
              <div className="pt-2">
                <p className="text-sm font-medium line-clamp-1">{video.video_title}</p>
                {video.channel && <p className="text-xs text-muted-foreground">{video.channel}</p>}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <VideoIcon className="h-8 w-8 mb-2" />
              <p className="text-sm">No video available for this exercise yet.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExerciseVideoDialog;
